import React, { useEffect, useMemo, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";
import { Bookmark, RefreshCw } from "lucide-react";

import { PageWrapper } from "../../../../../components/layout/container/Container";
import { Button } from "../../../../../components/buttons/Index";

import { useUserSavedPops } from "../hooks/useUserSavedPops";
import { useUserPopsViews } from "../../views/hooks/useUserPopsViews";

import ContentGrid from "../../../../../components/grids/content/ContentGrid";
import PopThumbnailCard from "../../../../../components/cards/pops/PopThumbnailCard";

import "./UserSavedPopsView.css";

const LIMIT = 12;
const MAX_VIEW_IDS_PER_REQUEST = 50;

const validId = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const id = Number(value);

  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const normalizeCount = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const count = Number(value);

  return Number.isFinite(count) && count >= 0 ? count : fallback;
};

const normalizeViewResult = (result) => {
  const popId = validId(result?.popId ?? result?.pop_id);

  if (!popId) return null;

  const realViews = normalizeCount(
    result.realViews ?? result.real_views ?? result.views,
  );

  const demoViews = normalizeCount(result.demoViews ?? result.demo_views);

  return {
    popId,
    realViews,
    demoViews,
    totalViews: normalizeCount(
      result.totalViews ?? result.total_views,
      realViews + demoViews,
    ),
  };
};

const getErrorMessage = (error) => {
  if (typeof error === "string") {
    return error;
  }

  return (
    error?.response?.data?.message ||
    error?.message ||
    "Unable to load saved Pops."
  );
};

const UserSavedPopsSkeleton = () => {
  return (
    <div
      className="user-saved-pops-grid__grid user-saved-pops-grid__skeleton-grid"
      aria-busy="true"
      aria-label="Loading saved Pops"
    >
      {Array.from({ length: LIMIT }, (_, index) => (
        <div key={index} className="user-saved-pops-grid__skeleton">
          <div className="user-saved-pops-grid__skeleton-thumbnail" />

          <div className="user-saved-pops-grid__skeleton-details">
            <div className="user-saved-pops-grid__skeleton-line" />

            <div className="user-saved-pops-grid__skeleton-line user-saved-pops-grid__skeleton-line--short" />
          </div>
        </div>
      ))}
    </div>
  );
};

const UserSavedPopsEmpty = ({ error, onRetry }) => {
  return (
    <div className="user-saved-pops-grid__empty">
      <Bookmark size={32} strokeWidth={1.5} />

      <h3>{error ? "Unable to load saved Pops" : "No saved Pops yet"}</h3>

      <p>{error || "Pops you save will appear here."}</p>

      {error && (
        <button
          type="button"
          className="user-saved-pops-grid__retry"
          onClick={onRetry}
        >
          <RefreshCw size={16} />
          Try Again
        </button>
      )}
    </div>
  );
};

export default function UserSavedPopsView() {
  const navigate = useNavigate();

  const {
    savedPops,
    loading,
    loadingMore,
    error,
    hasMore,
    total,
    loadMore,
    refresh,
  } = useUserSavedPops({
    limit: LIMIT,
    autoLoad: true,
  });

  const { getPopViewCounts } = useUserPopsViews();

  const [viewCounts, setViewCounts] = useState({});
  const [refreshVersion, setRefreshVersion] = useState(0);

  const mountedRef = useRef(false);
  const requestGenerationRef = useRef(0);

  const loadedViewIdsRef = useRef(new Set());
  const pendingViewIdsRef = useRef(new Set());

  const getPopViewCountsRef = useRef(getPopViewCounts);

  useEffect(() => {
    getPopViewCountsRef.current = getPopViewCounts;
  }, [getPopViewCounts]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      requestGenerationRef.current += 1;
    };
  }, []);

  const popItems = useMemo(() => {
    if (!Array.isArray(savedPops)) {
      return [];
    }

    const seen = new Set();

    return savedPops.filter((pop) => {
      const popId = validId(pop?.popId);

      if (!popId || seen.has(popId)) {
        return false;
      }

      seen.add(popId);

      return true;
    });
  }, [savedPops]);

  useEffect(() => {
    if (!popItems.length) {
      return;
    }

    const generation = requestGenerationRef.current;

    const ids = popItems.map((pop) => validId(pop.popId)).filter(Boolean);

    const missingIds = ids.filter((id) => {
      const key = String(id);

      return (
        !loadedViewIdsRef.current.has(key) &&
        !pendingViewIdsRef.current.has(key)
      );
    });

    if (!missingIds.length) {
      return;
    }

    const batches = [];

    for (
      let index = 0;
      index < missingIds.length;
      index += MAX_VIEW_IDS_PER_REQUEST
    ) {
      batches.push(missingIds.slice(index, index + MAX_VIEW_IDS_PER_REQUEST));
    }

    batches.forEach((batch) => {
      batch.forEach((id) => {
        pendingViewIdsRef.current.add(String(id));
      });
    });

    batches.forEach(async (batch) => {
      try {
        const results = await getPopViewCountsRef.current(batch);

        if (
          !mountedRef.current ||
          generation !== requestGenerationRef.current
        ) {
          return;
        }

        if (!Array.isArray(results)) {
          throw new Error("Invalid saved Pop view-count response.");
        }

        const nextCounts = {};

        results.forEach((result) => {
          const normalized = normalizeViewResult(result);

          if (!normalized) return;

          const key = String(normalized.popId);

          nextCounts[key] = normalized;
          loadedViewIdsRef.current.add(key);
        });

        if (Object.keys(nextCounts).length > 0) {
          setViewCounts((previous) => ({
            ...previous,
            ...nextCounts,
          }));
        }
      } catch (viewError) {
        console.error("Unable to retrieve saved Pop view counts:", viewError);
      } finally {
        if (generation === requestGenerationRef.current) {
          batch.forEach((id) => {
            pendingViewIdsRef.current.delete(String(id));
          });
        }
      }
    });
  }, [popItems, refreshVersion]);

  const displayPops = useMemo(() => {
    return popItems.map((pop) => {
      const key = String(pop.popId);
      const fetched = viewCounts[key];

      const fallbackRealViews = normalizeCount(
        pop.realViews ?? pop.real_views ?? pop.views,
      );

      const fallbackDemoViews = normalizeCount(pop.demoViews ?? pop.demo_views);

      const fallbackTotalViews = normalizeCount(
        pop.totalViews ?? pop.total_views,
        fallbackRealViews + fallbackDemoViews,
      );

      const realViews = fetched ? fetched.realViews : fallbackRealViews;

      const demoViews = fetched ? fetched.demoViews : fallbackDemoViews;

      const totalViews = fetched ? fetched.totalViews : fallbackTotalViews;

      return {
        ...pop,
        views: totalViews,
        realViews,
        demoViews,
        totalViews,
        isSaved: true,
      };
    });
  }, [popItems, viewCounts]);

  const hasPops = displayPops.length > 0;

  const isInitialLoading = loading && !hasPops;

  const savedCount =
    Number.isFinite(Number(total)) && Number(total) > 0
      ? Number(total)
      : popItems.length;

  const handleOpenPop = (pop) => {
    const popId = validId(pop?.popId);

    if (!popId) return;

    navigate(`/pops/${popId}`);
  };

  const handleLoadMore = async () => {
    if (loading || loadingMore || !hasMore) {
      return false;
    }

    return loadMore();
  };

  const handleRefresh = async () => {
    requestGenerationRef.current += 1;

    loadedViewIdsRef.current.clear();
    pendingViewIdsRef.current.clear();

    setViewCounts({});
    setRefreshVersion((previous) => previous + 1);

    return refresh();
  };

  const displayError = error ? getErrorMessage(error) : "";

  return (
    <PageWrapper>
      <section
        className="user-saved-pops-grid"
        aria-labelledby="user-saved-pops-title"
      >
        <div className="user-saved-pops-grid__header">
          <div className="user-saved-pops-grid__heading">
            <h2 id="user-saved-pops-title">Saved Pops ({savedCount})</h2>

            <p>Your collection of saved Pops.</p>
          </div>
        </div>

        {isInitialLoading ? (
          <UserSavedPopsSkeleton />
        ) : !hasPops ? (
          <UserSavedPopsEmpty error={displayError} onRetry={handleRefresh} />
        ) : (
          <>
            {displayError && (
              <p className="user-saved-pops-grid__error" role="alert">
                {displayError}
              </p>
            )}

            <ContentGrid
              items={displayPops}
              columns={6}
              tabletColumns={3}
              mobileColumns={2}
              gap={16}
              showEmpty={false}
              renderItem={(pop) => (
                <PopThumbnailCard
                  key={pop.popId ?? pop.popTag}
                  pop={pop}
                  mode="grid"
                  onClick={() => handleOpenPop(pop)}
                />
              )}
            />
          </>
        )}

        {hasMore && hasPops && (
          <div className="user-saved-pops-grid__footer">
            <Button
              value={loadingMore ? "Loading..." : "Load More"}
              variant="dark-outlined"
              disabled={loading || loadingMore}
              onClick={handleLoadMore}
            />
          </div>
        )}
      </section>
    </PageWrapper>
  );
}
